import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getBuildings,
  addInventory,
  updateInventory,
  deleteInventory,
} from "../../../../../../apis/Business/InventoryApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { getInventoryBuildingData } from "../../../../../../apis/Dropdown";
import { getTransportCountryData } from "../../../../../../apis/Dropdown";

const InventoryBuildingTable = ({ businessId, subcategory, subcategoryId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownData, setDropdownData] = useState([]);
  const [dropdownLocation, setDropdownLocation] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getInventoryBuildingData({
      setDropdownData: setDropdownData,
    });
    getTransportCountryData({
      setDropdownData: setDropdownLocation,
    });
    getBuildings({
      businessId: businessId,
      setObjects: setData,
      buildingName: subcategory,
      buildingId: subcategoryId,
    });
  }, [businessId, subcategory, subcategoryId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      updateInventory({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        building: subcategory,
        subcategory: "buildings",
        type_buildingmaterial: editedItem["type_buildingmaterial.value"]
          ? editedItem["type_buildingmaterial.value"].value
          : editedItem["type_buildingmaterial"]
          ? editedItem["type_buildingmaterial"].value
          : null,
        type_buildingcode: null,
        location_id: editedItem["location_id"] || null,
        type_chemical: null,
        type_vehicle: null,
        type_packaging: null,
        type_service: null,
        type_machinery: null,
        buildingId: subcategoryId,
        subcategoryId: editedItem["subcategory_obj"],
        location: editedItem["location.value"]
          ? editedItem["location.value"].value
          : editedItem["location"]
          ? editedItem["location"].value
          : null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData, subcategory, subcategoryId]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addInventory({
        businessId,
        ...newItem,
        type_buildingmaterial: newItem["type_buildingmaterial.value"]
          ? newItem["type_buildingmaterial.value"].value
          : null,
        type_buildingcode: null,
        location_id: newItem["location_id"] || null,
        type_chemical: null,
        type_vehicle: null,
        type_packaging: null,
        type_service: null,
        type_machinery: null,
        building: subcategory,
        subcategory: "buildings",
        buildingId: subcategoryId,
        location: newItem["location.value"]
          ? newItem["location.value"].value
          : null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData, subcategory, subcategoryId]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteInventory({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "80px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "80px",
      },
      {
        accessor: "nickname",
        header: t("INV_NICKNAME"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "80px",
      },
      {
        accessor: "manuel",
        header: t("Manual kgCO₂/m²"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIP_INV_BUILDING"),
      },
      {
        accessor: "location.value",
        header: t("PRODUCTION_COUNTRY"),
        type: "dropdown",
        dropdownData: dropdownLocation,
        disableSortBy: true,
        width: "110px",
        optional: true,
      },
      {
        accessor: "location_id",
        header: t("LOCATION_ID"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "90px",
        optional: true,
      },
      {
        accessor: "type_buildingmaterial.value",
        header: t("MATERIAL_PRODUCT_INV"),
        type: "dropdown",
        dropdownData: dropdownData,
        isCategory: true,
        disableSortBy: true,
        width: "110px",
        tipHeader: "Tip",
        tipBody: t("TIP_TYPE_BUILDING"),
        optional: true,
      },
      {
        accessor: "qty",
        header: t("QUANTITY"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("INV_AMOUNT_TIP"),
      },
      {
        accessor: "weight",
        header: t("INV_WEIGHT"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        tipHeader: "Tip",
        tipBody: t("TIP_WEIGHT_BUILDING"),
      },
      {
        accessor: "spacer2",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: true,
        subtotalUnit: "DKK",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
    ],
    [dropdownData, dropdownLocation, t]
  );

  const disableRules = {
    carbon: () => true,
    manuel: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "type_buildingmaterial.value": dropdownData,
      "location.value": dropdownLocation,
    }),
    [dropdownData, dropdownLocation]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default InventoryBuildingTable;
